// Alertas del piso del Home VL, compuestas en el front con lo que el tablero ya carga. Lógica PURA.
import type { SellerKpiRow, StoreKpiRow } from '@/api/services/retail-org/backoffice';
import type { CancellationItem } from '@/api/services/retail-wizard/backoffice';
import { daysBetweenMx, formatDateMx, mxDay } from '@/lib/dateHelpers';
import type { SalePromotionListItem } from '../../promotions/api/promotionsContract';
import { displayStatusOf } from '../../promotions/lib/promotionStatus';
import {
    LOW_PROGRESS_MIN_FLOOR_HOURS,
    PROMO_EXPIRING_MAX_DAYS,
    SELLER_LOW_MAX_ALERTS,
    SELLER_LOW_THRESHOLD,
    STORE_BEHIND_THRESHOLD,
} from '../constants';
import { hoursSinceOpening } from './homeMetrics';

export type FloorAlertKind =
    | 'STORE_BEHIND'
    | 'SELLER_LOW'
    | 'CANCELLATION_TODAY'
    | 'PROMO_EXPIRING';

export type FloorAlertSourceKey = 'stores' | 'sellers' | 'cancellations' | 'promotions';

/** Una fuente del tablero: cargando, caída, sin acceso o con sus datos. */
export type FloorAlertSource<T> =
    | { status: 'loading' }
    | { status: 'error' }
    | { status: 'unavailable' }
    | { status: 'ready'; data: T };

export interface FloorAlertSources {
    stores: FloorAlertSource<{
        rows: StoreKpiRow[];
        activeStoreIds: ReadonlySet<string> | null;
    }>;
    sellers: FloorAlertSource<SellerKpiRow[]>;
    cancellations: FloorAlertSource<CancellationItem[]>;
    promotions: FloorAlertSource<SalePromotionListItem[]>;
}

export interface FloorAlertLink {
    section: 'tiendas' | 'vendedores' | 'promociones';
    search?: { tienda?: string; vendedor?: string };
}

export interface FloorAlert {
    id: string;
    kind: FloorAlertKind;
    title: string;
    subtitle: string;
    link: FloorAlertLink | null;
}

export interface FloorAlertsResult {
    alerts: FloorAlert[];
    /** Fuentes que se pidieron y fallaron o que el usuario no puede leer. */
    unchecked: { source: FloorAlertSourceKey; reason: 'error' | 'unavailable' }[];
    /** Fuentes que siguen cargando. */
    loading: FloorAlertSourceKey[];
}

const SOURCE_ORDER: FloorAlertSourceKey[] = ['stores', 'sellers', 'promotions', 'cancellations'];

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

const byTitle = (a: FloorAlert, b: FloorAlert) => a.title.localeCompare(b.title, 'es');

const pct = (fraction: number) => `${String(Math.round(fraction * 100))}%`;

/** Avance contra la meta, o `null` si no hay meta o no hay vendido. */
function progressOf(row: { soldTodayCents: number | null; dailyGoalCents: number | null }) {
    if (row.dailyGoalCents === null || row.dailyGoalCents <= 0 || row.soldTodayCents === null) {
        return null;
    }
    return row.soldTodayCents / row.dailyGoalCents;
}

/** Al abrir el piso todos van en 0%; el avance contra la meta solo dice algo tras unas horas. */
function tooEarlyForProgress(now: Date): boolean {
    return hoursSinceOpening(now) < LOW_PROGRESS_MIN_FLOOR_HOURS;
}

function storesBehind(
    rows: StoreKpiRow[],
    activeStoreIds: ReadonlySet<string> | null,
    now: Date,
): FloorAlert[] {
    if (tooEarlyForProgress(now)) return [];
    return rows
        .filter((row) => activeStoreIds === null || activeStoreIds.has(row.storeId))
        .flatMap((row) => {
            const progress = progressOf(row);
            if (progress === null || progress >= STORE_BEHIND_THRESHOLD) return [];
            return [
                {
                    id: `store-${row.storeId}`,
                    kind: 'STORE_BEHIND' as const,
                    title: row.name,
                    subtitle: `${pct(progress)} de meta día · ${String(row.unitsToday ?? 0)} piezas`,
                    link: { section: 'tiendas' as const, search: { tienda: row.storeId } },
                },
            ];
        })
        .sort(byTitle);
}

function sellersLow(rows: SellerKpiRow[], now: Date): FloorAlert[] {
    if (tooEarlyForProgress(now)) return [];
    return rows
        .map((row) => ({ row, progress: progressOf(row) }))
        .filter(
            (entry): entry is { row: SellerKpiRow; progress: number } =>
                entry.progress !== null && entry.progress < SELLER_LOW_THRESHOLD,
        )
        .sort((a, b) => a.progress - b.progress)
        .slice(0, SELLER_LOW_MAX_ALERTS)
        .map(({ row, progress }) => ({
            id: `seller-${row.sellerId}`,
            kind: 'SELLER_LOW' as const,
            title: row.name,
            subtitle: `${row.storeName ?? 'Sin tienda'} · ${pct(progress)} · ${String(row.unitsToday ?? 0)} piezas`,
            link: { section: 'vendedores' as const, search: { vendedor: row.sellerId } },
        }))
        .sort(byTitle);
}

function promotionsExpiring(promos: SalePromotionListItem[], now: Date): FloorAlert[] {
    return promos
        .flatMap((promo) => {
            if (promo.validUntil === null || displayStatusOf(promo, now) !== 'ACTIVE') return [];
            const days = daysBetweenMx(now.toISOString(), promo.validUntil);
            if (days > PROMO_EXPIRING_MAX_DAYS) return [];
            return [
                {
                    id: `promo-${promo.promotionId}`,
                    kind: 'PROMO_EXPIRING' as const,
                    title: promo.name,
                    subtitle: `Vence ${formatDateMx(promo.validUntil)}`,
                    link: { section: 'promociones' as const },
                },
            ];
        })
        .sort(byTitle);
}

function cancellationsToday(items: CancellationItem[], now: Date): FloorAlert[] {
    const today = mxDay(now.toISOString());
    return items
        .filter((item) => item.cancelledAt !== null && mxDay(item.cancelledAt) === today)
        .map((item) => ({
            id: `cancellation-${item.saleId}`,
            kind: 'CANCELLATION_TODAY' as const,
            title: `${item.sellerName ?? 'Vendedor sin nombre'} · ${MXN.format(item.amountCents / 100)}`,
            subtitle: item.storeName ?? 'Tienda sin nombre',
            link: item.sellerId
                ? { section: 'vendedores' as const, search: { vendedor: item.sellerId } }
                : null,
        }))
        .sort(byTitle);
}

/** Arma las alertas de cada fuente lista; las que cargan o fallaron se reportan aparte. */
export function buildFloorAlerts(sources: FloorAlertSources, now: Date): FloorAlertsResult {
    const result: FloorAlertsResult = { alerts: [], unchecked: [], loading: [] };
    for (const key of SOURCE_ORDER) {
        const source = sources[key];
        if (source.status === 'loading') result.loading.push(key);
        else if (source.status !== 'ready')
            result.unchecked.push({ source: key, reason: source.status });
    }

    const { stores, sellers, promotions, cancellations } = sources;
    if (stores.status === 'ready') {
        result.alerts.push(...storesBehind(stores.data.rows, stores.data.activeStoreIds, now));
    }
    if (sellers.status === 'ready') result.alerts.push(...sellersLow(sellers.data, now));
    if (promotions.status === 'ready') {
        result.alerts.push(...promotionsExpiring(promotions.data, now));
    }
    if (cancellations.status === 'ready') {
        result.alerts.push(...cancellationsToday(cancellations.data, now));
    }
    return result;
}
